import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BillingContext, CatalogPlan } from "@/lib/api/organizations";
import { billingKeys } from "@/lib/api/billing/keys";
import {
  useBillingContextQuery,
  useOrgUsage,
  usePlanCatalog,
  usePlanRead,
} from "@/lib/api/billing/org-billing";
import { useOrgContextStore } from "components/main/stores/orgContextStore";
import {
  useIsCheckoutPending,
  usePendingCheckoutStore,
} from "@/stores/usePendingCheckoutStore";

const NO_PLANS: CatalogPlan[] = [];

/// The panel's reads for one org, their one reload, and the watch on a payment
/// that has not landed yet.
export function useBillingContext(orgId: string) {
  const client = useQueryClient();
  const read = usePlanRead(orgId).data;
  // A failed read is neither "no plan" nor a reason to offer Upgrade.
  const planRead: "loading" | "loaded" | "failed" = !read
    ? "loading"
    : read.failed
      ? "failed"
      : "loaded";
  const billing = useBillingContextQuery(orgId).data ?? null;
  const usage = useOrgUsage(orgId).data ?? null;
  const catalog = usePlanCatalog().data ?? NO_PLANS;

  // Payment may have landed before the period moved: an unapplied delivery on
  // the server, or this browser just back from checkout.
  const pendingLocally = useIsCheckoutPending(orgId);
  const clearPendingCheckout = usePendingCheckoutStore((s) => s.clearPending);
  const applying =
    billing !== null &&
    billing.subscription === null &&
    (billing.pending_events > 0 || pendingLocally);
  // The spinner's promise ("this will land by itself") is over; Subscribe
  // stays withheld because the stuck delivery may BE the payment.
  const stuck =
    billing !== null &&
    billing.subscription === null &&
    billing.stuck_events > 0;
  useEffect(() => {
    if (billing?.subscription) clearPendingCheckout(orgId);
  }, [billing?.subscription, clearPendingCheckout, orgId]);

  /// The switcher's badge reads `Org.plan_name` off the org list, so a plan
  /// change reloads that too.
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);

  const refreshBillingContext = useCallback(() => {
    void client.invalidateQueries({ queryKey: billingKeys.context(orgId) });
  }, [client, orgId]);

  const refreshUsage = useCallback(() => {
    void client.invalidateQueries({ queryKey: billingKeys.usage(orgId) });
  }, [client, orgId]);

  const reloadBilling = useCallback(() => {
    void client.invalidateQueries({ queryKey: billingKeys.plan(orgId) });
    refreshBillingContext();
    refreshUsage();
    void refreshOrgs();
  }, [client, orgId, refreshBillingContext, refreshUsage, refreshOrgs]);

  const patchBilling = useCallback(
    (patch: (context: BillingContext) => BillingContext) => {
      client.setQueryData<BillingContext>(
        billingKeys.context(orgId),
        (context) => context && patch(context),
      );
    },
    [client, orgId],
  );

  // The confirmation poller saw the period move, so everything here is stale.
  const confirmedAt = usePendingCheckoutStore((s) => s.confirmedAt[orgId]);
  const cardChangedAt = usePendingCheckoutStore((s) => s.cardChangedAt[orgId]);
  useEffect(() => {
    if (confirmedAt !== undefined || cardChangedAt !== undefined) {
      reloadBilling();
    }
  }, [confirmedAt, cardChangedAt, reloadBilling]);

  const [watchExpired, setWatchExpired] = useState(false);
  useEffect(() => {
    if (!applying) {
      setWatchExpired(false);
      return;
    }
    // Expiring re-runs this effect; without the gate it would restart the timer.
    if (watchExpired) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      reloadBilling();
      if (attempts >= 40) {
        window.clearInterval(timer);
        setWatchExpired(true);
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [applying, watchExpired, reloadBilling]);

  const recheck = useCallback(() => {
    setWatchExpired(false);
    reloadBilling();
  }, [reloadBilling]);

  return {
    plan: read?.plan ?? null,
    planRead,
    billing,
    usage,
    catalog,
    applying,
    stuck,
    watchExpired,
    recheck,
    confirmedAt,
    patchBilling,
    refreshBillingContext,
    refreshUsage,
    reloadBilling,
  };
}
